import React, { useMemo, useState, useEffect } from "react";
import { getYouTubeVideoId, parseCategories } from "../utils/helpers";
import { getApiUrl } from "../config/api";
import MetaTags from "../components/MetaTags";
import NewsSection from "../components/NewsSection";
import CategoryNewsSection from "../components/CategoryNewsSection";
import IndiaWorldNewsSection from "../components/IndiaWorldNewsSection";
import VideosSection from "../components/VideosSection";
import ReelsSection from "../components/ReelsSection";

function MainSite({ lang, articles, textData, imageVersion }) {
  if (!articles || articles.length === 0) return null;

  const nonEpaperArticles = useMemo(
    () => articles.filter((article) => !article.pdf),
    [articles]
  );

  const { topNews } = useMemo(() => {
    const validArticles = nonEpaperArticles.filter(
      (article) =>
        article.title &&
        article.title.trim() !== "" &&
        article.content &&
        article.content.trim() !== "" &&
        ((article.image && article.image.length > 0) ||
          article.youtubeUrl ||
          article.shortVideo)
    );

    const top = validArticles.slice(0, 4);

    return {
      topNews: top,
    };
  }, [nonEpaperArticles]);

  const [trendingFromApi, setTrendingFromApi] = useState([]);
  useEffect(() => {
    fetch(getApiUrl("TRENDING_NEWS"))
      .then((r) => r.json())
      .then((data) => {
        if (data.success && Array.isArray(data.posts)) {
          setTrendingFromApi(data.posts);
        }
      })
      .catch(() => {});
  }, [articles]);

  const localNews = useMemo(() => {
    return nonEpaperArticles.filter((article) => {
      if (!article.categories) return false;
      const categories = parseCategories(article.categories);
      const lowerCaseCategories = categories.map((c) => String(c).toLowerCase());
      return lowerCaseCategories.includes("localnews");
    }).slice(0, 4);
  }, [nonEpaperArticles]);

  const gujaratNews = useMemo(() => {
    return nonEpaperArticles.filter((article) => {
      if (!article.categories) return false;
      const categories = parseCategories(article.categories);
      const lowerCaseCategories = categories.map((c) => String(c).toLowerCase());
      return lowerCaseCategories.includes("gujarat");
    }).slice(0, 4);
  }, [nonEpaperArticles]);

  const indiaNews = useMemo(() => {
    return nonEpaperArticles.filter((article) => {
      if (!article.categories) return false;
      const categories = parseCategories(article.categories);
      const lowerCaseCategories = categories.map((c) => String(c).toLowerCase());
      return lowerCaseCategories.includes("india");
    }).slice(0, 5);
  }, [nonEpaperArticles]);

  const worldNews = useMemo(() => {
    return nonEpaperArticles.filter((article) => {
      if (!article.categories) return false;
      const categories = parseCategories(article.categories);
      const lowerCaseCategories = categories.map((c) => String(c).toLowerCase());
      return (
        lowerCaseCategories.includes("world") ||
        lowerCaseCategories.includes("worldnews")
      );
    }).slice(0, 6);
  }, [nonEpaperArticles]);

  const uniqueVideos = useMemo(() => {
    const videoMap = new Map();
    nonEpaperArticles.forEach((article) => {
      if (article.youtubeUrl) {
        const videoId = getYouTubeVideoId(article.youtubeUrl);
        if (!videoMap.has(videoId)) {
          videoMap.set(videoId, article);
        }
      }
    });
    return Array.from(videoMap.values());
  }, [nonEpaperArticles]);

  const uniqueShorts = useMemo(() => {
    const shortMap = new Map();
    nonEpaperArticles.forEach((article) => {
      if (article.shortVideo) {
        const videoId = getYouTubeVideoId(article.shortVideo);
        if (!shortMap.has(videoId)) {
          shortMap.set(videoId, article);
        }
      }
    });
    return Array.from(shortMap.values());
  }, [nonEpaperArticles]);

  return (
    <main className="flex-grow">
      <MetaTags
        title="Home"
        description="Get the latest news from Ahmedabad, Gujarat, India and around the world. Live updates on business, sports, entertainment, and more."
      />
      <NewsSection
        topNews={topNews}
        trendingNews={trendingFromApi}
        headings={textData.headings}
        lang={lang}
        imageVersion={imageVersion}
      />
      <CategoryNewsSection
        localNews={localNews}
        gujaratNews={gujaratNews}
        indiaNews={indiaNews}
        localheading={textData.headings.localNews}
        gujaratHeading={textData.headings.gujaratNews}
        indiaHeading={textData.headings.indiaNews}
        lang={lang}
        imageVersion={imageVersion}
      />
      <IndiaWorldNewsSection
        indiaNews={indiaNews}
        worldNews={worldNews}
        indiaHeading={textData.headings.indiaNews}
        worldHeading={textData.headings.worldNews}
        lang={lang}
        imageVersion={imageVersion}
      />
      <VideosSection
        videos={uniqueVideos}
        headings={textData.headings}
        lang={lang}
      />
      <ReelsSection
        reels={uniqueShorts}
        headings={textData.headings}
        lang={lang}
      />
    </main>
  );
}

export default MainSite;
